import type {
	AssociationMembershipResponse,
} from '~~/openapi-client/gen/schemas'

// Speichert die aktuell ausgewählten Mitglieder
const selectedMemberIds = ref<string[]>([])

export const useMembers = () => {
	const { selectedAssociation } = useAssociations()
	const { $api } = useNuxtApp()

	const memberCount = computed(() => members.value?.length)
	const boardMemberCount = computed(() => members.value?.filter(m => m.role.toString() !== 'MEMBER').length)

	const selectedMembers = computed(() => members.value?.filter(m => selectedMemberIds.value.includes(m.id)) || [])

	const selectedMembersEmpty = computed(() => selectedMembers.value.length === 0)

	/**
	 * Setzt die Auswahl der Mitglieder zurück.
	 */
	const resetSelection = () => selectedMemberIds.value = []

	const { refresh, data: members } = useAsyncData(
		'members',
		() => {
			if (selectedAssociation.value) {
				return $api.associationMembershipApi.findAllByAssociationId(selectedAssociation.value.id)
			} else {
				return Promise.resolve(undefined)
			}
		},
		{
			default: () => [] as AssociationMembershipResponse[],
			watch: [selectedAssociation],
			transform: user => (user?.data) as AssociationMembershipResponse[],
		},
	)

	/**
	 * Überprüft, ob ein Mitglied in der Liste der ausgewählten Mitglieder ist.
	 * @param memberId
	 */
	const isSelected = (memberId: string) => selectedMembers.value.some(m => m.id === memberId)

	/**
	 * Überprüft, ob ein Mitglied eine E-Mail-Adresse hat.
	 * @param member
	 */
	const hasEmail = (member: AssociationMembershipResponse) => !!member.contactInformationResponse?.email

	/**
	 * Überprüft, ob ein Mitglied ein Systembenutzer ist.
	 * ADMIN oder MANAGE_USERS sind Systembenutzer.
	 * @param member
	 */
	const isSystemUser = (member: AssociationMembershipResponse) => member.role.toString() !== 'MEMBER'

	/**
	 * Überprüft, ob ein Mitglied ein Vorstandsmitglied ist.
	 * @param member
	 */
	const isBoardMember = (member: AssociationMembershipResponse) => member.role.toString() === 'ADMIN'

	/**
	 * Fügt ein Mitglied zu den ausgewählten Mitgliedern hinzu oder entfernt es, wenn es bereits ausgewählt ist.
	 * @param member
	 */
	const toggleSelected = (member: AssociationMembershipResponse) => {
		if (isSelected(member.id)) {
			selectedMemberIds.value = selectedMemberIds.value.filter(m => m !== member.id)
		} else {
			selectedMemberIds.value.push(member.id)
		}
	}

	/**
	 * Löscht die aktuell ausgewählten Mitglieder.
	 */
	const deleteMembership = async () => {
		const response = await $api.associationMembershipApi.deleteMemberships(selectedMembers.value.map(member => member.id))
		if (response.data.failed.length > 0) {
			throw new Error(response.data.failed.map(f => `Mitglied ${f.id} (${f.error})`).join(', '))
		}
	}

	const inviteMembers = async () => {
		const filteredMembers = selectedMembers.value.filter(m => !isSystemUser(m) && hasEmail(m))
		await Promise.all(
			filteredMembers.map(member =>
				$api.associationMembershipApi.updateMemberToManageUsers(member.id),
			),
		)
	}

	const downloadImportTemplate = async () => {
		const response = await $api.associationMembershipApi.downloadCsvTemplate()
		await downloadFile('mitglieder-import-vorlage.csv', 'text/csv', response.data)
	}

	const exportMembers = async () => {
		if (!selectedAssociation.value) {
			throw createError({ statusCode: 400, statusMessage: 'Kein Verein wurde ausgewählt' })
		}
		const response = await $api.associationMembershipApi.exportAsCsv(selectedAssociation.value.id)
		await downloadFile(`mitglieder-export-${selectedAssociation.value?.name || 'verein'}.csv`, 'text/csv', response.data)
	}

	return {
		members,
		memberCount,
		boardMemberCount,
		selectedMembers,
		selectedMembersEmpty,
		refresh,
		resetSelection,
		isSelected,
		hasEmail,
		isBoardMember,
		isSystemUser,
		toggleSelected,
		deleteMembership,
		inviteMembers,
		downloadImportTemplate,
		exportMembers,
	}
}
